/**
 * @file `EmailPreview` — a framed preview of the rendered HTML email: a
 *       From/Subject header over a white "paper" body that renders the
 *       composer's HTML, with a scope-aware unsubscribe footer.
 * @module @labcraft/admin/ui/emails/email-preview
 *
 * The paper stays light in both themes (it is an email, not app chrome). The
 * `footer` flag is scope-aware per the redesign: a Campaign preview shows the
 * real per-recipient unsubscribe footer (what the recipient gets); an
 * Individual preview shows none (the non-marketing send carries none). The
 * `bodyHtml` is the sanitizer-safe output of {@link renderMarkdown}, so the
 * preview equals the sent email.
 */

import type { JSX } from 'react'

/**
 * Props for {@link EmailPreview}.
 *
 * @property from - The actual send-From for this kind (operator setting);
 *   falls back to the bare brand name while settings load.
 * @property subject - The composed subject (header line).
 * @property bodyHtml - The rendered, sanitizer-safe HTML body.
 * @property device - Preview width: `desktop` (600px) or `mobile` (340px).
 * @property footer - Whether to show the unsubscribe footer (Campaign only).
 */
export type EmailPreviewProps = {
  from?: string
  subject: string
  bodyHtml: string
  device: 'desktop' | 'mobile'
  footer: boolean
}

/** Render the framed email preview. */
export function EmailPreview(props: EmailPreviewProps): JSX.Element {
  const { from, subject, bodyHtml, device, footer } = props
  return (
    <div className="email-frame">
      <div className="email-frame__head">
        <div className="email-frame__row">
          <span className="email-frame__k">From</span>
          <span className="email-frame__v">{from ?? 'LabCraft'}</span>
        </div>
        <div className="email-frame__row">
          <span className="email-frame__k">Subject</span>
          <span className="email-frame__v email-frame__subj">
            {subject || '(no subject)'}
          </span>
        </div>
      </div>
      <div className="email-frame__stage">
        <div
          className={
            device === 'mobile'
              ? 'email-frame__paper email-frame__paper--mobile'
              : 'email-frame__paper'
          }>
          {bodyHtml ? (
            // Safe: bodyHtml is renderMarkdown's constrained output (raw HTML
            // + images suppressed), and the daemon re-sanitizes on send.
            <div
              className="email-body"
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
          ) : (
            <p className="email-empty">
              Start writing — your email preview appears here.
            </p>
          )}
          {footer && (
            <div className="email-frame__footer">
              You&rsquo;re receiving this because you have a LabCraft account.
              <br />
              <a href="#unsubscribe">Unsubscribe</a> · LabCraft
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
